import {
  DeleteOutlined,
  EditOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Popconfirm, Space, Tooltip } from 'antd';
import { Button, Status } from '@insight-health-ai/roseate/components';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { getApiErrorMessage, magicFlowApi } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import type { Flow, FlowRunPriority, MagicFlowOption } from 'src/models';

type UseMagicFlowFlowsControllerParams = {
  orgUuid?: string;
  hasOrgSelected: boolean;
  scopedHeaders: Record<string, string | boolean>;
  onTriggerManualRun?: (flow: Flow) => void;
};

type FlowListResponse = {
  flows: Flow[];
  page?: number;
  page_size?: number;
  total?: number;
};

type FlowRunPriorityListResponse = {
  priorities?: FlowRunPriority[];
  flow_run_priorities?: FlowRunPriority[];
};

export const useMagicFlowFlowsController = ({
  orgUuid,
  hasOrgSelected,
  scopedHeaders,
  onTriggerManualRun,
}: UseMagicFlowFlowsControllerParams) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: flows = [],
    isLoading: isLoadingFlows,
    isFetching: isFetchingFlows,
  } = useQuery({
    queryKey: ['magicflow-flows', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<FlowListResponse>('/flow', {
          headers: scopedHeaders,
          params: {
            page: 1,
            page_size: 200,
            active_only: false,
          },
        })
        .then((res) => res.data.flows || []),
  });

  const { data: priorities = [] } = useQuery({
    queryKey: ['magicflow-flow-run-priorities', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<FlowRunPriorityListResponse>('/flow_run_priority', {
          headers: scopedHeaders,
        })
        .then(
          (res) => res.data.priorities || res.data.flow_run_priorities || []
        ),
  });

  const deleteFlowMutation = useMutation({
    mutationFn: (flowId: string) =>
      magicFlowApi.delete(`/flow/${flowId}`, {
        headers: scopedHeaders,
      }),
    onSuccess: () => {
      showToastSuccess('Flow deleted successfully');
      queryClient.invalidateQueries({
        queryKey: ['magicflow-flows', orgUuid],
      });
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) || 'Failed to delete flow'
      );
    },
  });

  const flowTableData = useMemo(
    () =>
      flows.map((flow) => ({
        ...flow,
        key: flow.flow_id,
      })),
    [flows]
  );

  const flowColumns: ColumnsType<Flow & { key: string }> = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: 240,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      width: 110,
      render: (value, record) => (
        <Space>
          <span className="font-mono">v{value ?? '-'}</span>
          {record.is_active ? (
            <Status appearance="success" label="active" size="xs" />
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Default Priority',
      dataIndex: 'default_priority',
      width: 140,
      render: (value) => value || '-',
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      width: 200,
      render: (value) =>
        value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      render: (_, record) => (
        <Space size={8}>
          <Tooltip title="Edit">
            <span>
              <Button
                variant="outlined"
                appearance="secondary"
                size="sm"
                icon={<EditOutlined />}
                onClick={() =>
                  navigate(`/superadmin/magicflow/flows/${record.flow_id}`)
                }
              />
            </span>
          </Tooltip>
          {onTriggerManualRun && record.is_active ? (
            <Tooltip title="Run now">
              <span>
                <Button
                  variant="outlined"
                  appearance="secondary"
                  size="sm"
                  icon={<PlayCircleOutlined />}
                  onClick={() => onTriggerManualRun(record)}
                />
              </span>
            </Tooltip>
          ) : null}
          <Popconfirm
            title="Delete flow"
            description="This flow will be soft deleted."
            onConfirm={() => deleteFlowMutation.mutate(record.flow_id)}
            okButtonProps={{ loading: deleteFlowMutation.isPending }}
          >
            <span>
              <Button
                variant="outlined"
                appearance="danger"
                size="sm"
                icon={<DeleteOutlined />}
              />
            </span>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const priorityOptions: MagicFlowOption[] = useMemo(
    () =>
      (priorities || []).map((priority) => ({
        value: priority.key,
        label: priority.name || priority.key,
      })),
    [priorities]
  );

  return {
    flows,
    flowTableData,
    flowColumns,
    isLoadingFlows,
    isFetchingFlows,
    priorityOptions,
    openCreateFlow: () => navigate('/superadmin/magicflow/flows/new'),
  };
};
